import { COLORS } from "../../../assets/theme";

/** Nav-local tokens. Kept out of global COLORS: these only read on the dark rail. */
export const NAV = {
  widthExpanded: 288,
  widthRail: 76,
  rowHeightDesktop: 42,
  rowHeightTouch: 48,

  surface: COLORS.BlackBackground,
  surfaceRaised: "#232323",
  surfaceSunken: "rgba(0, 0, 0, 0.28)",
  border: "rgba(255, 255, 255, 0.08)",
  borderStrong: "rgba(255, 255, 255, 0.14)",

  textActive: "#FFFFFF",
  textIdle: "rgba(255, 255, 255, 0.62)",
  textMuted: "rgba(255, 255, 255, 0.38)",

  hover: "rgba(255, 255, 255, 0.06)",
  pressed: "rgba(255, 255, 255, 0.10)",

  accent: COLORS.GOLD,
  accentSoft: "rgba(163, 148, 85, 0.16)",
  accentSofter: "rgba(163, 148, 85, 0.08)",
  accentBorder: "rgba(163, 148, 85, 0.42)",
  accentGlow:
    "0 0 0 1px rgba(163, 148, 85, 0.30), 0 4px 14px rgba(163, 148, 85, 0.16)",

  flyoutShadow: "0 12px 40px rgba(0, 0, 0, 0.55)",

  spring: { type: "spring" as const, stiffness: 520, damping: 40, mass: 0.85 },
  springSoft: { type: "spring" as const, stiffness: 320, damping: 34, mass: 0.9 },
  ease: "cubic-bezier(0.22, 1, 0.36, 1)",
};

/** Strips inherited button/anchor chrome from nav rows. */
export const resetRow = {
  appearance: "none",
  border: 0,
  background: "none",
  font: "inherit",
  textAlign: "left" as const,
  textDecoration: "none",
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
};

export const focusRing = {
  "&:focus-visible": {
    outline: `2px solid ${NAV.accentBorder}`,
    outlineOffset: "2px",
  },
};

export const transition = (...props: string[]) =>
  props.map((p) => `${p} 160ms ${NAV.ease}`).join(", ");

/** Raised panel shared by the palette, rail flyout, and shortcut picker. */
export const overlayPaper = (radius = 16) => ({
  backgroundColor: NAV.surfaceRaised,
  backgroundImage: "none",
  border: `1px solid ${NAV.borderStrong}`,
  borderRadius: `${radius}px`,
  boxShadow: NAV.flyoutShadow,
});

/** Square ghost icon button. */
export const ghostButton = (size: number, radius = 11) => ({
  ...resetRow,
  ...focusRing,
  width: size,
  height: size,
  flexShrink: 0,
  borderRadius: `${radius}px`,
  display: "grid",
  placeItems: "center",
  color: NAV.textIdle,
  backgroundColor: "transparent",
  transition: transition("background-color", "color"),
  "@media (hover: hover)": {
    "&:hover": { backgroundColor: NAV.hover, color: NAV.textActive },
  },
});

/** Interactive nav row: consistent height, hover, press and focus states. */
export const rowSx = ({
  height,
  radius = 11,
  gap = 1.25,
  px = 1.25,
}: {
  height: number;
  radius?: number;
  gap?: number;
  px?: number;
}) => ({
  ...resetRow,
  ...focusRing,
  display: "flex",
  alignItems: "center",
  gap,
  px,
  minHeight: height,
  borderRadius: `${radius}px`,
  color: NAV.textIdle,
  transition: transition("background-color", "color"),
  "@media (hover: hover)": {
    "&:hover": { backgroundColor: NAV.hover, color: NAV.textActive },
  },
  "&:active": { backgroundColor: NAV.pressed },
});
